<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import DialogLarge from '@/components/DialogLarge.vue'
import Button from '@/components/Button.vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import { useRouter, useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'

type ImportType = 'PF' | 'PJ' | 'BOTH' | null

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
  (e: 'start-import', value: Exclude<ImportType, null>): void
}>()

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const toast = useToast()

const { mobile } = useDisplay()
const isMobile = computed(() => mobile.value)

const selectedType = ref<ImportType>('PF')

const implementedTypes: Exclude<ImportType, null>[] = ['PF', 'PJ', 'BOTH']

const proxyOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    emit('update:modelValue', value)

    if (!value) {
      emit('close')
    }
  },
})

const options = computed(() => [
  {
    value: 'PF' as const,
    title: t('contacts.import.dialog.firstStep.personalContact.title'),
    description: t(
      'contacts.import.dialog.firstStep.personalContact.description'
    ),
    iconClass: 'import-option__icon import-option__icon--circle',
    icon: 'mdi-account',
    disabled: false,
  },
  {
    value: 'PJ' as const,
    title: t('contacts.import.dialog.firstStep.corporativeContact.title'),
    description: t(
      'contacts.import.dialog.firstStep.corporativeContact.description'
    ),
    iconClass: 'import-option__icon import-option__icon--square',
    icon: 'mdi-office-building',
    disabled: false,
  },
  {
    value: 'BOTH' as const,
    title: t(
      'contacts.import.dialog.firstStep.personalContacWithCorporativeContact.title'
    ),
    description: t(
      'contacts.import.dialog.firstStep.personalContacWithCorporativeContact.description'
    ),
    iconLeft: 'mdi-account',
    iconRight: 'mdi-office-building',
    disabled: false,
  },
])

const canSubmit = computed(() => !!selectedType.value)

function isImplemented(type: ImportType): type is Exclude<ImportType, null> {
  return !!type && implementedTypes.includes(type)
}

function selectOption(optionValue: Exclude<ImportType, null>) {
  if (options.value.find((option) => option.value === optionValue)?.disabled) {
    return
  }

  selectedType.value = optionValue
}

function handleConfirm() {
  if (!selectedType.value) return

  if (isImplemented(selectedType.value)) {
    emit('start-import', selectedType.value)

    router.push({
      name: 'personal-contacts-import',
      params: {
        id: route.params.id,
      },
      query: {
        type: selectedType.value,
      },
    })

    return
  }

  toast.info(t('general.notImplementedYet'), {
    timeout: 3000,
  })
}

function handleCancel() {
  proxyOpen.value = false
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      selectedType.value = 'PF'
    }
  }
)
</script>

<template>
  <DialogLarge
    v-model="proxyOpen"
    :title-padding-bottom="8"
    :close="false"
    max-width="1200px"
    content-class="import-contacts-dialog__content"
    actions-class="import-contacts-dialog__actions"
  >
    <template #title>
      <p :class="isMobile ? 'text-body-1 font-weight-semibold' : 'text-h6'">
        {{ $t('contacts.import.dialog.firstStep.title') }}
      </p>
    </template>
    <template #default>
      <div class="import-contacts-dialog__body">
        <p :class="isMobile ? 'text-caption mb-4' : 'text-body-1 mb-6'">
          {{ $t('contacts.import.dialog.firstStep.subTitle') }}
        </p>

        <div class="import-options">
          <button
            v-for="option in options"
            :key="option.value"
            type="button"
            class="import-option"
            :class="{
              'import-option--selected': selectedType === option.value,
              'import-option--disabled': option.disabled,
            }"
            :disabled="option.disabled"
            @click="selectOption(option.value)"
          >
            <div class="import-option__visual">
              <template v-if="option.value !== 'BOTH'">
                <div :class="option.iconClass">
                  <v-icon :size="isMobile ? 16 : 40" color="white">
                    {{ option.icon }}
                  </v-icon>
                </div>
              </template>

              <template v-else>
                <div class="import-option__combo">
                  <div class="import-option__icon import-option__icon--circle">
                    <v-icon :size="isMobile ? 16 : 40" color="white">
                      {{ option.iconLeft }}
                    </v-icon>
                  </div>

                  <div class="import-option__icon import-option__icon--square">
                    <v-icon :size="isMobile ? 16 : 40" color="white">
                      {{ option.iconRight }}
                    </v-icon>
                  </div>
                </div>
              </template>
            </div>

            <h3
              class="import-option__title"
              :class="
                isMobile
                  ? 'text-body-1 font-weight-semibold mb-2'
                  : 'text-h6 mt-6'
              "
            >
              {{ option.title }}
            </h3>

            <p
              class="import-option__description"
              :class="isMobile ? 'text-caption' : 'text-caption mt-6'"
            >
              {{ option.description }}
            </p>
          </button>
        </div>
      </div>
    </template>

    <template #actions>
      <div class="import-contacts-dialog__footer">
        <Button
          :class="isMobile ? 'primaryOutline' : 'tertiaryFlat'"
          @click="handleCancel"
        >
          {{ $t('general.cancel') }}
        </Button>

        <Button
          class="primaryFlat"
          :disabled="!canSubmit"
          @click="handleConfirm"
        >
          {{ $t('contacts.import.dialog.startImport') }}
        </Button>
      </div>
    </template>
  </DialogLarge>
</template>

<style scoped>
.font-weight-semibold {
  font-weight: 600 !important;
}

.import-contacts-dialog__body {
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0;
}

.import-contacts-dialog__subtitle {
  margin-bottom: 24px;
  color: var(--opacity-text--secondary, rgba(0, 0, 0, 0.6));
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans') !important;
}

.import-options {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.import-option {
  position: relative;
  display: grid;
  grid-template-rows: 120px 56px 1fr;
  justify-items: center;
  align-items: start;
  text-align: center;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 24px;
  padding: 24px;
  min-height: 300px;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    opacity 0.2s ease,
    transform 0.2s ease;
}

.import-option:hover:not(.import-option--disabled) {
  transform: translateY(-2px);
}

.import-option--selected {
  border: 2px solid #8dc63f;
  box-shadow: none;
}

.import-option--disabled {
  opacity: 0.72;
  cursor: not-allowed;
}

.import-option__visual {
  width: 100%;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.import-option__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #8dc63f;
  flex-shrink: 0;
}

.import-option__icon--circle {
  width: 120px;
  height: 120px;
  border-radius: 999px;
}

.import-option__icon--square {
  width: 120px;
  height: 120px;
  border-radius: 24px;
}

.import-option__combo {
  position: relative;
  width: 220px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.import-option__combo .import-option__icon--circle {
  width: 120px;
  height: 120px;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
}

.import-option__combo .import-option__icon--square {
  width: 120px;
  height: 120px;
  border-radius: 24px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
  border: 4px solid #fff;
}

.import-option__title {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--standart-theme---EVE-expert-primary, #8dc63f);
  text-align: center;
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans') !important;
}

.import-option:not(.import-option--selected) .import-option__title {
  color: rgba(0, 0, 0, 0.87);
}

.import-option__description {
  width: 100%;
  max-width: 320px;
  color: var(--opacity-text--secondary, rgba(0, 0, 0, 0.6));
  text-align: center;
  font-family: var(--Typography-fontFamilies-Open-Sans, 'Open Sans') !important;
}

:deep(.import-contacts-dialog__content) {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  overflow-y: auto;
  min-height: 0;
}

:deep(.import-contacts-dialog__actions) {
  padding: 0 !important;
  flex: 0 0 auto;
  background: #fff;
}

.import-contacts-dialog__footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding-top: 24px;
}

/* DESKTOP */
@media (min-width: 961px) {
  :deep(.import-contacts-dialog__content) {
    max-height: calc(90vh - 170px);
  }
}

/* MOBILE/TABLET */
@media (max-width: 960px) {
  .import-contacts-dialog__body {
    padding: 0;
  }

  .import-contacts-dialog__subtitle {
    margin: 0 0 24px;
  }

  .import-options {
    grid-template-columns: 1fr;
    margin-bottom: 16px;
  }

  .import-option {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    min-height: unset;
    padding: 16px;
    border-radius: 16px;
    text-align: left;
  }

  .import-option__visual {
    width: 100%;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 16px;
  }

  /* ÍCONES MOBILE */
  .import-option__icon--circle,
  .import-option__icon--square {
    width: 32px;
    height: 32px;
  }

  .import-option__icon--circle {
    border-radius: 999px;
  }

  .import-option__icon--square {
    border-radius: 8px;
  }

  /* COMBO PF + PJ */
  .import-option__combo {
    width: 56px;
    height: 32px;
    position: relative;
    display: block;
  }

  .import-option__combo .import-option__icon--circle,
  .import-option__combo .import-option__icon--square {
    width: 32px;
    height: 32px;
    position: absolute;
    top: 0;
  }

  .import-option__combo .import-option__icon--circle {
    left: 0;
    z-index: 1;
  }

  .import-option__combo .import-option__icon--square {
    left: 24px;
    right: auto;
    z-index: 2;
    border: 1px solid #fff;
    border-radius: 8px;
  }

  .import-option__title {
    width: 100%;
    min-height: unset;
    display: block;
    color: var(--standart-theme---EVE-expert-primary, #8dc63f);
    text-align: left;
    font-feature-settings: 'liga' off;
    font-weight: 600 !important;
  }

  .import-option:not(.import-option--selected) .import-option__title {
    color: rgba(0, 0, 0, 0.87);
  }

  .import-option__description {
    width: 100%;
    max-width: none;
    margin: 0;
    color: var(--opacity-text--secondary, rgba(0, 0, 0, 0.6));
    text-align: left;
    font-family: var(
      --Typography-fontFamilies-Open-Sans,
      'Open Sans'
    ) !important;
  }

  :deep(.import-contacts-dialog__content) {
    max-height: calc(100vh - 240px);
  }

  .import-contacts-dialog__footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 0 0;
    background: #fff;
  }

  .import-contacts-dialog__footer :deep(.v-btn) {
    width: 100%;
    min-width: 0 !important;
  }
}
</style>
